import { ArrowDownTrayIcon, ArrowPathIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";

import DashboardWidget from "../components/dashboard/DashboardWidget";
import PlanLockedHint from "../components/PlanLockedHint";
import Popover, { MenuItem } from "../components/reports/Popover";
import ReportFilterBar from "../components/reports/ReportFilterBar";
import { formatWindow, updatedLabel } from "../components/reports/reportFormat";
import { apiErrorMessage } from "../services/api";
import { getCurrentUser } from "../services/authService";
import { hasFeature } from "../services/features";
import { can } from "../services/permissions";
import { exportDashboard, fetchDashboard } from "../services/dashboardService";
import { fetchReportFilterOptions } from "../services/reportService";
import { WELCOME_KEY } from "../services/signupService";

const DEFAULT_PERIOD = "last_30_days";
const DEFAULT_COMPARE = "previous";
const DATE = /^\d{4}-\d{2}-\d{2}$/;

/** The applied filters, from the URL (the only source of truth). */
const readFilters = (params) => {
  const from = DATE.test(params.get("from") ?? "") ? params.get("from") : "";
  const to = DATE.test(params.get("to") ?? "") ? params.get("to") : "";
  const custom = Boolean(from && to);

  return {
    period: custom ? "" : params.get("period") || DEFAULT_PERIOD,
    from: custom ? from : "",
    to: custom ? to : "",
    branches: params.get("branches") ? params.get("branches").split(",").filter(Boolean) : [],
    currency: params.get("currency") ?? "",
    compare: params.get("compare") || DEFAULT_COMPARE,
  };
};

const writeFilters = (filters) => {
  const params = new URLSearchParams();
  const set = (name, value) => value && params.set(name, value);

  set("period", filters.period && filters.period !== DEFAULT_PERIOD ? filters.period : "");
  set("from", filters.period ? "" : filters.from);
  set("to", filters.period ? "" : filters.to);
  set("branches", filters.branches.join(","));
  set("currency", filters.currency);
  set("compare", filters.compare !== DEFAULT_COMPARE ? filters.compare : "");

  return params;
};

/** What the widgets are asked for: the applied filters minus empties. */
const apiFilters = (filters) => ({
  ...(filters.period ? { period: filters.period } : { from: filters.from, to: filters.to }),
  branches: filters.branches,
  currency: filters.currency,
  compare: filters.compare,
});

/**
 * Business dashboard home (spec 07, layout `ref/dashboard.jpg`).
 *
 * The server decides the variant — owner / manager / reception / provider
 * (AC07) — and the widgets; each widget loads on its own from the reports
 * engine and opens its report (with the same filters) when clicked.
 */
export default function Dashboard() {
  const { t, i18n } = useTranslation();
  const user = getCurrentUser();
  const [searchParams, setSearchParams] = useSearchParams();
  const applied = useMemo(() => readFilters(searchParams), [searchParams]);
  const [draft, setDraft] = useState(applied);
  const [layout, setLayout] = useState(null);
  const [options, setOptions] = useState(null);
  const [error, setError] = useState("");
  const [updated, setUpdated] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [welcome, setWelcome] = useState(() => {
    try {
      return sessionStorage.getItem(WELCOME_KEY) === "1";
    } catch {
      return false;
    }
  });

  useEffect(() => setDraft(applied), [applied]);

  useEffect(() => {
    let cancelled = false;

    fetchDashboard()
      .then((data) => !cancelled && setLayout(data))
      .catch((err) => !cancelled && setError(apiErrorMessage(err, t("dashboard.loadFailed"))));
    fetchReportFilterOptions()
      .then((data) => !cancelled && setOptions(data))
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [t]);

  const filters = useMemo(() => apiFilters(applied), [applied]);
  // Every widget asks again on a new `n`: Refresh skips the server cache;
  // back to this tab asks quietly (the server recomputes only on a change).
  const [reload, setReload] = useState({ n: 0, refresh: false });
  const [refreshing, setRefreshing] = useState(false);
  // New filters or a reload, new figures: the freshness line starts over.
  useEffect(() => setUpdated(null), [filters, reload]);
  const onLoaded = useCallback((meta) => {
    setRefreshing(false);
    if (meta?.as_of)
      setUpdated((current) => ({
        // Widgets are cached separately: the line reports the oldest one.
        at: current?.at && new Date(current.at) < new Date(meta.as_of) ? current.at : meta.as_of,
        cached: Boolean(current?.cached || meta.cached),
        timezone: meta.timezone ?? current?.timezone,
        window: meta.window ?? current?.window,
        compareWindow: meta.compare_window ?? current?.compareWindow,
      }));
  }, []);
  useEffect(() => {
    const onVisible = () =>
      document.visibilityState === "visible" && setReload((r) => ({ n: r.n + 1, refresh: false }));
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, []);
  const refresh = () => {
    setRefreshing(true);
    setReload((r) => ({ n: r.n + 1, refresh: true }));
  };
  // "Just now" turns into the time after a minute: re-render every 30 s.
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(timer);
  }, []);

  const dismissWelcome = () => {
    setWelcome(false);
    try {
      sessionStorage.removeItem(WELCOME_KEY);
    } catch {
      // Nothing to clean up.
    }
  };

  const variant = layout?.variant;
  const business = variant === "owner" || variant === "manager";
  const kpis = (layout?.widgets ?? []).filter((w) => w.size === "kpi");
  const panels = (layout?.widgets ?? []).filter((w) => w.size !== "kpi");
  const dirty = JSON.stringify(draft) !== JSON.stringify(applied);

  const runExport = async (format, close) => {
    close();
    setExporting(true);
    try {
      await exportDashboard(format, filters);
    } catch (err) {
      setError(apiErrorMessage(err, t("dashboard.v2.exportFailed")));
    } finally {
      setExporting(false);
    }
  };

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold">
            {t(business ? "dashboard.v2.title" : `dashboard.v2.titles.${variant ?? "none"}`)}
          </h1>
          <p className="text-sm text-ink-2">
            {t("dashboard.subtitle", { business: user?.business?.name ?? t("app.business") })}
          </p>
        </div>
        {layout?.variant && (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            {updated && (
              <p className="flex items-center gap-1.5 text-caption text-ink-2">
                <span className="h-2 w-2 rounded-full bg-accent" aria-hidden="true" />
                {updatedLabel(updated.at, updated.cached, now, i18n.language, t)}
                {updated.timezone ? ` · ${updated.timezone}` : ""}
                {updated.window ? ` · ${formatWindow(updated.window, i18n.language)}` : ""}
                {updated.compareWindow
                  ? ` · ${t("reports.shell.comparedWith", { window: formatWindow(updated.compareWindow, i18n.language) })}`
                  : ""}
              </p>
            )}
            <button
              type="button"
              className="flex items-center gap-1 text-caption font-bold text-accent-text disabled:opacity-60"
              onClick={refresh}
              disabled={refreshing}
            >
              <ArrowPathIcon className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} aria-hidden="true" />
              {t("reports.shell.refresh")}
            </button>
          </div>
        )}
      </div>

      {/* Shown once, right after a self sign-up. */}
      {welcome && (
        <div className="relative mb-5 rounded-card border border-accent-line bg-accent-wash p-4 pe-12">
          <button
            type="button"
            onClick={dismissWelcome}
            aria-label={t("dashboard.welcomeDismiss")}
            className="absolute end-3 top-3 rounded-md p-1 text-ink-2 hover:bg-surface"
          >
            <XMarkIcon className="size-5" aria-hidden="true" />
          </button>
          <h2 className="text-card-title font-bold text-ink">{t("dashboard.welcomeTitle")}</h2>
          <p className="mt-1 text-sm text-ink-2">{t("dashboard.welcomeText")}</p>
          <ol className="mt-3 flex flex-wrap gap-2 text-sm">
            {[
              ["/branches/create", "dashboard.welcomeBranch"],
              ["/services", "dashboard.welcomeServices"],
              ["/providers", "dashboard.welcomeProviders"],
            ].map(([to, key], i) => (
              <li key={to}>
                <Link
                  to={to}
                  className="inline-flex items-center gap-2 rounded-full border border-accent-line bg-surface px-3 py-1.5 font-semibold text-ink hover:border-accent"
                >
                  <span className="flex size-5 items-center justify-center rounded-full bg-accent text-xs text-accent-on">
                    {i + 1}
                  </span>
                  {t(key)}
                </Link>
              </li>
            ))}
          </ol>
        </div>
      )}

      {error && (
        <div className="mb-4 rounded-lg border border-danger-line bg-danger-wash px-3 py-2 text-sm text-danger-text">
          {error}
        </div>
      )}

      {business && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <ReportFilterBar
            draft={draft}
            setDraft={setDraft}
            options={options}
            report={{ finance: layout?.finance, filters: [], cash_view: false, date_bases: [] }}
            meta={null}
            dirty={dirty}
            onApply={() => setSearchParams(writeFilters(draft))}
          />
          {can(user, "reports.export") && !hasFeature(user, "reports_export") && (
            <PlanLockedHint feature="reports_export" label={t("dashboard.v2.export")} />
          )}
          {can(user, "reports.export") && hasFeature(user, "reports_export") && (
            <Popover
              icon={ArrowDownTrayIcon}
              label={exporting ? t("dashboard.v2.exporting") : t("dashboard.v2.export")}
              panelClassName="w-56"
            >
              {(close) => (
                <div>
                  <MenuItem onClick={() => runExport("pdf", close)}>{t("dashboard.v2.exportPdf")}</MenuItem>
                  <MenuItem onClick={() => runExport("xlsx", close)}>{t("dashboard.v2.exportXlsx")}</MenuItem>
                </div>
              )}
            </Popover>
          )}
        </div>
      )}

      {layout?.no_branch_access && (
        <div
          role="status"
          className="mb-4 rounded-lg border border-warning-line bg-warning-wash px-3 py-2 text-dense text-warning-text"
        >
          {t("reports.noBranchAccess")}
        </div>
      )}

      {layout && !variant && <p className="card p-6 text-center text-ink-2">{t("dashboard.v2.nothing")}</p>}

      {kpis.length > 0 && (
        <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-6">
          {kpis.map((w) => (
            <DashboardWidget
              key={w.key}
              widgetKey={w.key}
              size={w.size}
              title={w.title}
              filters={filters}
              onLoaded={onLoaded}
              reload={reload}
            />
          ))}
        </div>
      )}

      {panels.length > 0 && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          {panels.map((w) => (
            <DashboardWidget
              key={w.key}
              widgetKey={w.key}
              size={w.size}
              title={w.title}
              filters={filters}
              onLoaded={onLoaded}
              reload={reload}
            />
          ))}
        </div>
      )}

      {!layout && !error && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="card h-28 animate-pulse" />
          ))}
        </div>
      )}
    </div>
  );
}
